import React, { useState, useCallback } from 'react';
import { Box, Text, Button, Input, FormControl, FormErrorMessage, VStack } from '@chakra-ui/react';
import StepHeader from './StepHeader';
import { useDispatch, useSelector } from 'react-redux';
import { reschedulecall, schedulecall } from '../../collection/ApiCollection';
import { toast } from 'react-toastify';
import { nextStep, updateFormData } from '../../store/formSlice';
import { TimePicker, DatePicker, ConfigProvider } from 'antd';
import { authtemplogin } from "../../store/Authentication";
import dayjs from 'dayjs';
const ScheduleFirstCallStep = () => {
    const { formData } = useSelector((state) => state.form);
    const loginData = useSelector(state => state.Authlogin.dataTemp?.userDetail ? state.Authlogin.dataTemp : {});
    const { scheduleDate, scheduleTime, firstName, age: formAge, experiences } = formData;

    const [date, setDate] = useState(scheduleDate || '');
    const [time, setTime] = useState(scheduleTime || '');
    const [name, setName] = useState(firstName || '');
    const [age, setAge] = useState(formAge || '');
    const [errors, setErrors] = useState({ date: '', time: '', name: '', age: '' });
    const [apiError, setApiError] = useState('');
    const [loading, setLoading] = useState(false);
    const dispatch = useDispatch()

    const handleDateChange = useCallback((e) => {
        setDate(e.target.value);
        if (errors.date) setErrors((prev) => ({ ...prev, date: '' }));
    }, [errors.date]);
    const handleTimeChange = useCallback((e) => {
        setTime(e.target.value);
        if (errors.time) setErrors((prev) => ({ ...prev, time: '' }));
    }, [errors.time]);
    const handleAgeChange = useCallback((e) => {
        setAge(e.target.value);
        if (errors.age) setErrors((prev) => ({ ...prev, age: '' }));
    }, [errors.age]);
    const handleNameChange = useCallback((e) => {
        setName(e.target.value);
        if (errors.name) setErrors((prev) => ({ ...prev, name: '' }));
    }, [errors.name]);

    const validate = useCallback(() => {
        let valid = true;
        let newErrors = { date: '', time: '', name: '', age: '' };
        if (!date.trim()) {
            newErrors.date = 'Date is required';
            valid = false;
        }
        if (!time.trim()) {
            newErrors.time = 'Time is required';
            valid = false;
        }
        if (!name.trim()) {
            newErrors.name = 'Name is required';
            valid = false;
        }
        if (!age || isNaN(Number(age)) || Number(age) <= 0) {
            newErrors.age = 'Valid age is required';
            valid = false;
        }
        setErrors(newErrors);
        return valid;
    }, [date, time, name, age]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setApiError('');
        if (validate()) {
            setLoading(true);
            try {
                // Combine date and time into a single Date object for scheduleTime
                const dateTimeString = `${date}T${time}`;
                const dateTime = new Date(dateTimeString);
                const unixTimestamp = Math.floor(dateTime.getTime() / 1000); // seconds
                // For scheduleDate, use the date at 00:00:00
                const dateOnly = new Date(`${date}T00:00:00`);
                const unixDate = Math.floor(dateOnly.getTime() / 1000); // seconds
                let res = await schedulecall({
                    scheduleDate: unixDate,
                    scheduleTime: unixTimestamp,
                    receptorName: name,
                    receptorAge: age,
                    agenda: experiences
                });
                let requestCallId = res.data.data._id
                const updatedAuthLogin = {
                    ...loginData,
                    userDetail: {
                        ...loginData.userDetail,
                        isScheduled: true
                    }
                };
                dispatch(authtemplogin(updatedAuthLogin));
                let response = await reschedulecall({ requestCallId, scheduleList: formData.scheduleList, fallbackOnEmpty: true })
                toast.success(res.message || 'The first call has been scheduled successfully.')
                dispatch(updateFormData({ requestCallId, scheduleDate: date, scheduleTime: time }))
                dispatch(nextStep())
            } catch (err) {
                setApiError(err?.message || 'Failed to schedule call. Please try again.');
                toast.error(err?.message || 'Failed to schedule call. Please try again.');
            } finally {
                setLoading(false);
            }
        }
    };

    return (
        <Box maxW={{ base: '100%', md: '3xl' }} mx="auto" p={{ base: 4, md: 6 }} textAlign="center">
            <StepHeader
                title="Schedule the First Call"
                subtitle={`Ready to schedule ${name || 'First Name'}'s first call?`}
            />
            <form onSubmit={handleSubmit}>
                <VStack spacing={4} mb={4} mx={4} align="stretch">
                    <FormControl isInvalid={!!errors.date} w="100%" maxW="500px" mx="auto">
                        <ConfigProvider theme={{ token: { colorPrimary: '#108084' } }}>
                            <DatePicker
                                value={date ? dayjs(date) : null}
                                onChange={(_, dateString) => setDate(dateString)}
                                format="YYYY-MM-DD"
                                style={{ width: '100%' }}
                                placeholder="Select date"
                                disabled={loading}
                                size="large"
                            />
                        </ConfigProvider>
                        <FormErrorMessage>{errors.date}</FormErrorMessage>
                    </FormControl>
                    <FormControl isInvalid={!!errors.time} w="100%" maxW="500px" mx="auto">
                        <ConfigProvider theme={{ token: { colorPrimary: '#108084' } }}>
                            <TimePicker
                                value={time ? dayjs(time, 'HH:mm') : null}
                                onChange={(_, timeString) => setTime(timeString)}
                                format="HH:mm"
                                style={{ width: '100%' }}
                                placeholder="Select time"
                                disabled={loading}
                                size="large"
                            />
                        </ConfigProvider>
                        <FormErrorMessage>{errors.time}</FormErrorMessage>
                    </FormControl>
                    <FormControl isInvalid={!!errors.name} w="100%" maxW="500px" mx="auto">
                        <Input
                            placeholder='Name of call recipient'
                            size='md'
                            value={name}
                            onChange={handleNameChange}
                            disabled={!!firstName || loading}
                            w="100%"
                            aria-label="Recipient name"
                            autoFocus={!!date && !!time && !name}
                        />
                        <FormErrorMessage>{errors.name}</FormErrorMessage>
                    </FormControl>
                    <FormControl isInvalid={!!errors.age} w="100%" maxW="500px" mx="auto">
                        <Input
                            placeholder='Age of call recipient'
                            size='md'
                            type='number'
                            value={age}
                            onChange={handleAgeChange}
                            disabled={!!formAge || loading}
                            w="100%"
                            aria-label="Recipient age"
                            autoFocus={!!date && !!time && !!name && !age}
                        />
                        <FormErrorMessage>{errors.age}</FormErrorMessage>
                    </FormControl>
                </VStack>
                {apiError && (
                    <Text color="red.500" mb={2}>{apiError}</Text>
                )}
                <Button
                    type="submit"
                    mt={4}
                    px="30px"
                    isLoading={loading}
                    disabled={loading}
                >
                    Schedule First Call
                </Button>
            </form>
            <Text mt={6} fontSize={{ base: '16px', md: '18px' }} color="black">
                <Text as="span" fontWeight="bold">Not sure yet?</Text> You can schedule later from your dashboard
            </Text>
        </Box>
    );
};

export default ScheduleFirstCallStep; 